/**
 * Header Component
 *
 * Adaptive header that changes structure based on LayoutTheme.
 * - modern-cards: Clean header with centered logo
 * - matrix-terminal: ASCII art banner
 * - mac-os-classic: Classic menu bar
 * - executive-dashboard: Minimal top bar
 */

import { useLayoutTheme } from '@/contexts/LayoutThemeContext'
import { Container } from '@/components/ui/Container'
import { useAuth } from '@/contexts/AuthContext'

export function Header() {
  const { themeConfig } = useLayoutTheme()
  const { user } = useAuth()

  // Modern Cards Layout
  if (themeConfig.id === 'modern-cards') {
    return (
      <header
        style={{
          backgroundColor: 'var(--color-surface)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
          padding: 'var(--spacing-lg) 0',
        }}
      >
        <Container size="xl">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
              <h1
                style={{
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  color: 'var(--color-text-primary)',
                  margin: 0,
                }}
              >
                Agustin Musi
              </h1>
              <span
                style={{
                  fontSize: '0.875rem',
                  color: 'var(--color-text-secondary)',
                }}
              >
                Full-Stack Engineer
              </span>
            </div>
            <div style={{ display: 'flex', gap: 'var(--spacing-md)', alignItems: 'center' }}>
              <span style={{ color: 'var(--color-text-secondary)' }}>
                {user?.displayName || 'Guest'}
              </span>
            </div>
          </div>
        </Container>
      </header>
    )
  }

  // Matrix Terminal Layout
  if (themeConfig.id === 'matrix-terminal') {
    return (
      <header
        style={{
          backgroundColor: '#000',
          color: '#0f0',
          padding: 'var(--spacing-lg) 0',
          fontFamily: 'monospace',
          borderBottom: '1px solid #0f0',
        }}
      >
        <Container size="xl">
          <pre style={{ margin: 0, fontSize: '0.875rem' }}>
            {`
╔════════════════════════════════════════════════════════╗
║  AGUSTIN_MUSI.SYSTEM.INIT()                           ║
║  > Full-Stack Engineer | Security Researcher          ║
║  > Status: ${user ? 'AUTHENTICATED' : 'PUBLIC_ACCESS'}                       ║
╚════════════════════════════════════════════════════════╝
            `.trim()}
          </pre>
        </Container>
      </header>
    )
  }

  // Mac OS Classic Layout
  if (themeConfig.id === 'mac-os-classic') {
    return (
      <header
        style={{
          backgroundColor: '#ddd',
          borderBottom: '2px solid #000',
          padding: 'var(--spacing-sm) 0',
        }}
      >
        <Container size="xl">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-lg)',
              fontSize: '0.875rem',
              fontFamily: 'Chicago, Geneva, sans-serif',
            }}
          >
            <span style={{ fontWeight: 'bold' }}>🍎 File</span>
            <span>Edit</span>
            <span>View</span>
            <span>Special</span>
            <div style={{ marginLeft: 'auto' }}>
              {user?.displayName || 'Guest'}
            </div>
          </div>
        </Container>
      </header>
    )
  }

  // Executive Dashboard Layout
  if (themeConfig.id === 'executive-dashboard') {
    return (
      <header
        style={{
          backgroundColor: 'transparent',
          borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
          padding: 'var(--spacing-md) 0',
        }}
      >
        <Container size="xl">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <h1
              style={{
                fontSize: '1.25rem',
                fontWeight: 600,
                color: 'var(--color-text-primary)',
                margin: 0,
              }}
            >
              AM
            </h1>
            <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
              {user?.displayName || 'Guest View'}
            </div>
          </div>
        </Container>
      </header>
    )
  }

  // Fallback
  return null
}
